// React core hooks
import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Tabulator } from 'react-tabulator/lib/types/TabulatorTypes';
import { useCodeManagement } from '@/domains/system-management/hooks/useCodeManagement';
// Types
import type { ButtonState, Code, Params } from '@/domains/system-management/types/code';
// Plugins and utilities
import NoticeMessage from '@/plugin/noticemessage/noticemessage';
import ButtonGroup from '@/shared/components/ui/ButtonGroup/ButtonGroup';
import ContainerCard from '@/shared/components/ui/ContainerCard/ContainerCard';
import DetailForm from '@/shared/components/ui/DetailForm/DetailForm';
import Filtering from '@/shared/components/ui/Filtering/Filtering';
// UI Components
import {
  type ColumnDefinition,
  ReactTabulator,
} from '@/shared/components/ui/LocalizedTabulator/LocalizedTabulator';
import LogList from '@/shared/components/ui/LogList/LogList';
import Select from '@/shared/components/ui/Select/Select';
// Custom hooks (business logic and data management)
import useCommonCodes from '@/shared/hooks/useCommonCodes';
import { formatFullDateTimeDDMMYYYY } from '@/shared/utils/date';

const CodeManagement = () => {
  const { t } = useTranslation();

  // tabulator top
  const commonColumnProps = {
    hozAlign: 'center' as const,
    headerHozAlign: 'center' as const,
    resizable: false,
  };

  const columnsHistory = [
    {
      title: t('cmn > no'),
      formatter: (cell: Tabulator.CellComponent) => {
        const row = cell.getRow();
        const page = row.getTable().getPage();
        const pageSize = row.getTable().getPageSize();
        return (Number(page) - 1) * Number(pageSize) + row.getPosition(true);
      },
      width: 65,
      headerSort: false,
      ...commonColumnProps,
    },
    {
      title: t('user > id'),
      field: 'id',
      widthGrow: 1,
      headerSort: false,
      visible: false,
      ...commonColumnProps,
    },
    {
      title: t('code > code group'),
      field: 'upper_code',
      widthGrow: 1,
      headerSort: false,
      ...commonColumnProps,
    },
    {
      title: t('code > code'),
      field: 'lower_code',
      widthGrow: 1,
      headerSort: false,
      ...commonColumnProps,
    },
    {
      title: t('code > ENG name'),
      field: 'eng',
      widthGrow: 2,
      headerSort: false,
      ...commonColumnProps,
      formatter: (cell: Tabulator.CellComponent) => {
        const data = cell.getRow().getData();
        return data.lower_code ? `　└　${cell.getValue()}` : cell.getValue();
      },
    },
    {
      title: t('code > IND name'),
      field: 'ind',
      widthGrow: 2,
      headerSort: false,
      ...commonColumnProps,
      formatter: (cell: Tabulator.CellComponent) => {
        const data = cell.getRow().getData();
        return data.lower_code ? `　└　${cell.getValue()}` : cell.getValue();
      },
    },
  ];

  const tbRef = useRef<Tabulator | null>(null);
  const searchRef = useRef<HTMLInputElement | null>(null);
  const codeRef = useRef<HTMLInputElement | null>(null);
  const codeGroupRef = useRef<HTMLInputElement | null>(null);

  //Disabled
  const [disabled, setDisabled] = useState<boolean>(true);
  const [disabledCode, setDisabledCode] = useState<boolean>(true);
  const [disabledCodeGroup, setDisabledCodeGroup] = useState<boolean>(true);

  //Change state
  const [hasChangesUpdate, setHasChangesUpdate] = useState<boolean>(false);
  const [hasChangesCreate, setHasChangesCreate] = useState<boolean>(false);
  const hasChangesUpdateRef = useRef(hasChangesUpdate);
  const hasChangesCreateRef = useRef(hasChangesCreate);
  const [newId, setNewId] = useState<string | null>(null);
  const [isNewClicked, setIsNewClicked] = useState<boolean>(false);
  const [selectedCode, setSelectedCode] = useState<{ id: string | null }>({ id: null });
  const selectedCodeRef = useRef(selectedCode);
  const [selectedIsCodeGroup, setSelectedIsCodeGroup] = useState<string | null>(null);

  useEffect(() => {
    hasChangesUpdateRef.current = hasChangesUpdate;
    hasChangesCreateRef.current = hasChangesCreate;
    selectedCodeRef.current = selectedCode;
  }, [hasChangesUpdate, hasChangesCreate, selectedCode]);

  // Partial<Code> allows us to keep only some fields at a time
  // e.g. during create/update forms where not all fields are required yet
  const [formValues, setFormValues] = useState<Partial<Code>>({});

  const emptyDetail = () => {
    setFormValues({
      id: null,
      lower_code: '',
      upper_code: '',
      eng: '',
      ind: '',
      sort_order: null,
      usage: '',
      description: '',
      codeType: '',
    });
  };

  //Button states
  const [buttonState, setButtonState] = useState<ButtonState>({
    confirm: true,
    cancel: true,
    delete: true,
    create: false,
  });

  const disableAllButtons = () => {
    setButtonState({
      confirm: true,
      cancel: true,
      delete: true,
      create: true,
    });
  };

  const enableInitialButtons = () => {
    disableAllButtons();
    setIsNewClicked(false);
    setButtonState((prevState) => ({
      ...prevState,
      create: false,
    }));
  };

  const enableUPDATEButtons = () => {
    disableAllButtons();
    setButtonState((prevState) => ({
      ...prevState,
      cancel: false,
      delete: false,
      create: false,
    }));
  };

  const enableRegisterButtons = () => {
    disableAllButtons();
    setButtonState((prevState) => ({
      ...prevState,
      cancel: false,
    }));
  };

  // CRUD callbacks
  const reloadCallback = () => {
    enableInitialButtons();
    tbRef.current?.deselectRow();
    emptyDetail();
    setDisabled(true);
    setDisabledCode(true);
    setDisabledCodeGroup(true);
    setHasChangesUpdate(false);
    setSelectedCode({ id: null });
    setIsNewClicked(false);
    setHasChangesCreate(false);
    setNewId(null);
  };
  const updateCallback = () => {
    enableUPDATEButtons();
    setIsNewClicked(false);
    setHasChangesUpdate(false);
    setSelectedCode({ id: null });
  };

  // API hooks
  const [queryParams, setQueryParams] = useState<Params>({
    deletion: '001002',
    usage: '002001',
  });

  const [optionParams] = useState<string>('upper-code=001&upper-code=002');

  const {
    useCodes,
    useActiveCodes,
    useCode,
    useCreateCode,
    useCreateLowerCode,
    useUpdateCode,
    useUpdateLowerCode,
    useDeleteCode,
  } = useCodeManagement(queryParams);

  const { data: codesData } = useCodes();
  const { data: activeCodes } = useActiveCodes();
  const { data: codeData, error: codeError } = useCode(selectedCode?.id ?? '');
  const createCodeMutation = useCreateCode((responseData?: { id: string }) => {
    reloadCallback();
    const newUserId = responseData?.id ?? null;
    setNewId(newUserId);
  });
  const createLowerCodeMutation = useCreateLowerCode((responseData?: { id: string }) => {
    reloadCallback();
    const newUserId = responseData?.id ?? null;
    setNewId(newUserId);
  });
  const updateCodeMutation = useUpdateCode(
    (responseData?: { id: string; updated_by: string; updated_time: string }) => {
      updateCallback();
      const newUserId = responseData?.id ?? null;
      setNewId(newUserId);
    }
  );
  const updateLowerCodeMutation = useUpdateLowerCode(
    (responseData?: { id: string; updated_by: string; updated_time: string }) => {
      const newUserId = responseData?.id ?? null;
      setNewId(newUserId);
      updateCallback();
    }
  );
  const deleteCodeMutation = useDeleteCode(() => {
    reloadCallback();
  });
  const { commonListData } = useCommonCodes({ optionParams });

  const optionsRadioFilterDeleted = commonListData?.['001']
    ? [
        { value: 'All', label: t('cmn > all'), code: 'All' },
        ...(commonListData['001']?.code?.map((code, index) => ({
          value: code ?? '',
          label: commonListData['001']?.name[index] ?? '',
          code: code ?? '',
        })) || []),
      ]
    : [];

  const optionsRadioFilterUsage = commonListData?.['002']
    ? [
        { value: 'All', label: t('cmn > all'), code: 'All' },
        ...(commonListData['002']?.code?.map((code, index) => ({
          value: code ?? '',
          label: commonListData['002']?.name[index] ?? '',
          code: code ?? '',
        })) || []),
      ]
    : [];

  // Tabulator options
  const languageTabulator = () => {
    const datalanguage = {
      pagination: {
        first: t('cmn > first page'), //text for the first page button
        first_title: t('cmn > first page'), //tooltip text for the first page button
        last: t('cmn > last page'),
        last_title: t('cmn > last page'),
        prev: t('cmn > page before'),
        prev_title: t('cmn > page before'),
        next: t('cmn > next page'),
        next_title: t('cmn > next page'),
      },
    };
    return datalanguage;
  };

  const optionsTabulator = {
    debugInvalidOptions: true,
    pagination: true,
    movableRows: false,
    resizableRows: false,
    locale: 'ko',
    langs: {
      ko: languageTabulator(),
    },
    index: 'id',
    paginationSize: 10,
    selectableRows: false,
    rowHeight: 41,
    footerElement: `<div id="footer-bottom" style="padding: 0 20px 0 0; text-align: right;">${t(
      'cmn > total'
    )} ${codesData?.length || 0} ${t('cmn > results')}</div>`,
    selectableRowsCheck: (row: Tabulator.RowComponent) => {
      return !row.getElement().classList.contains('tabulator-selected');
    },
    placeholder: `<span style='text-align:center;'>${t('msg > no data')}</span>`,
  };

  useEffect(() => {
    if (searchRef.current) {
      searchRef.current.focus();
    }
  }, []);

  useEffect(() => {
    if (codeData) {
      setFormValues((prevValues) => ({
        ...prevValues,
        eng: codeData?.eng,
        ind: codeData?.ind,
        upper_code: codeData?.upper_code,
        lower_code: codeData?.lower_code,
        usage: codeData?.usage,
        description: codeData?.description,
        sort_order: codeData?.sort_order,
      }));
    }
  }, [codeData]);

  useEffect(() => {
    if (codeError) {
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      new NoticeMessage(t('msg > load data fail'));
    }
  }, [codeError, t]);

  // Handlers
  const handleRowSelected = (row: Tabulator.RowComponent) => {
    if (hasChangesCreateRef.current || hasChangesUpdateRef.current) {
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      const message = new NoticeMessage(t('msg > flush confirm'), {
        mode: 'confirm',
      });
      message
        .confirmClicked()
        .then(() => {
          tbRef.current?.deselectRow();
          row.select();
          row.getElement().classList.add('custom-hover');

          const rowData = row.getData();
          setSelectedCode({
            id: rowData.id,
          });
          setDisabled(false);
          if (rowData.lower_code !== null) {
            setDisabledCode(false);
            setDisabledCodeGroup(true);
            setFormValues((prevValues) => ({
              ...prevValues,
              codeType: 'Code',
            }));
            requestAnimationFrame(() => {
              if (codeRef.current) {
                codeRef.current.focus();
              }
            });
          } else if (rowData.lower_code == null) {
            setDisabledCodeGroup(false);
            setDisabledCode(true);
            setFormValues((prevValues) => ({
              ...prevValues,
              codeType: 'Code Group',
            }));
            requestAnimationFrame(() => {
              if (codeGroupRef.current) {
                codeGroupRef.current.focus();
              }
            });
          }
          setHasChangesUpdate(false);
          setHasChangesCreate(false);
          setIsNewClicked(false);
        })
        .catch(() => {
          row.deselect();

          const prevId = selectedCodeRef.current?.id;
          if (prevId) {
            const prevRow = tbRef.current?.getRow(prevId) || null;
            prevRow?.select();
            prevRow?.getElement().classList.add('custom-hover');
          }
        });
    } else {
      tbRef.current?.deselectRow();
      row.select();
      row.getElement().classList.add('custom-hover');

      const rowData = row.getData();
      setSelectedCode((cur) => ({ ...cur, id: rowData.id }));
      setFormValues({
        id: rowData.id,
        eng: rowData.eng,
        ind: rowData.ind,
        upper_code: rowData.upper_code,
        lower_code: rowData.lower_code,
        codeType: rowData.codeType,
        usage: rowData.usage,
        description: rowData.description,
        sort_order: rowData.sort_order,
      });
      setDisabled(false);
      enableUPDATEButtons();
      setIsNewClicked(false);
      if (rowData.lower_code !== null) {
        setDisabledCode(false);
        setDisabledCodeGroup(true);
        setFormValues((prevValues) => ({
          ...prevValues,
          codeType: 'Code',
        }));
        requestAnimationFrame(() => {
          if (codeRef.current) {
            codeRef.current.focus();
          }
        });
      } else if (rowData.lower_code == null) {
        setDisabledCodeGroup(false);
        setDisabledCode(true);
        setFormValues((prevValues) => ({
          ...prevValues,
          codeType: 'Code Group',
        }));
        requestAnimationFrame(() => {
          if (codeGroupRef.current) {
            codeGroupRef.current.focus();
          }
        });
      }
    }

    requestAnimationFrame(() => {
      if (!disabledCodeGroup && codeGroupRef.current) {
        codeGroupRef.current.focus();
      } else if ((!disabledCode && codeRef.current) || isNewClicked) {
        codeRef.current?.focus();
      }
    });
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target as HTMLInputElement;
    setFormValues((prevValues) => ({
      ...prevValues,
      [name]: value,
    }));

    if (isNewClicked) {
      setHasChangesCreate(true);
    } else {
      setHasChangesUpdate(true);
    }

    if (selectedCode?.id) {
      if (!isNewClicked) {
        setHasChangesUpdate(true);
      }
    }
  };

  const handleOnChangeInputSelect = useCallback(({ target }: { target: { value: string } }) => {
    const { value } = target;
    setSelectedIsCodeGroup(value);
  }, []);

  const handleSearch = useCallback(
    (
      inputVal: string | null = null,
      radioVal: string | null = null,
      radioUsageVal: string | null = null
    ) => {
      const params: Params = {};
      if (inputVal) params.input = inputVal;
      if (radioVal && radioVal !== 'All') params.deletion = radioVal;
      if (radioUsageVal && radioUsageVal !== 'All') params.usage = radioUsageVal;
      if (selectedIsCodeGroup) params.upper_code = selectedIsCodeGroup;

      setQueryParams(params);
    },
    [selectedIsCodeGroup]
  );

  const handleCancelButtonClick = () => {
    if (hasChangesUpdate) {
      // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
      const message = new NoticeMessage(t('msg > flush confirm'), {
        mode: 'confirm',
      });
      message.confirmClicked().then(() => {
        tbRef.current?.deselectRow();
        emptyDetail();
        setIsNewClicked(false);
        setDisabled(true);
        setDisabledCode(true);
        setHasChangesUpdate(false);
        setDisabledCodeGroup(true);
        enableInitialButtons();
      });
    } else if (isNewClicked) {
      if (hasChangesCreate) {
        // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
        const message = new NoticeMessage(t('msg > flush confirm'), {
          mode: 'confirm',
        });
        message.confirmClicked().then(() => {
          emptyDetail();
          setIsNewClicked(false);
          setDisabledCode(true);
          setDisabledCodeGroup(true);
          setDisabled(true);
          enableInitialButtons();
          setHasChangesCreate(false);
        });
      } else {
        emptyDetail();
        setIsNewClicked(false);
        setDisabled(true);
        setDisabledCode(true);
        setDisabledCodeGroup(true);
        enableInitialButtons();
        setHasChangesCreate(false);
        setSelectedCode({ id: null });
      }
    } else {
      reloadCallback();
    }
  };
  const handleNewButtonClick = () => {
    if (selectedCode?.id) {
      tbRef.current?.deselectRow();
      setDisabledCode(false);
      setDisabledCodeGroup(true);
      emptyDetail();
      setFormValues((prevValues) => ({
        ...prevValues,
        codeType: 'Code',
        upper_code: codeData?.upper_code ?? '',
      }));
      setHasChangesUpdate(false);
      enableRegisterButtons();
      setIsNewClicked(true);
      requestAnimationFrame(() => {
        if (codeRef.current) {
          codeRef.current.focus();
        }
      });
    } else {
      tbRef.current?.deselectRow();
      emptyDetail();
      setFormValues((prevValues) => ({
        ...prevValues,
        codeType: 'Code Group',
      }));

      setDisabled(false);
      setDisabledCodeGroup(false);
      setDisabledCode(true);
      setIsNewClicked(true);
      enableRegisterButtons();
      setSelectedCode({ id: null });
      setHasChangesUpdate(false);
      requestAnimationFrame(() => {
        if (codeGroupRef.current) {
          codeGroupRef.current.focus();
        }
      });
    }
  };

  const handleRegistButtonClick = () => {
    if (selectedCode?.id) {
      createLowerCodeMutation.mutateAsync(formValues);
    } else {
      createCodeMutation.mutateAsync(formValues);
    }
  };

  const handleConfirmButtonClick = () => {
    if (!formValues.lower_code) {
      updateCodeMutation.mutateAsync({
        id: String(selectedCode?.id) ?? '',
        data: formValues,
      });
    } else {
      updateLowerCodeMutation.mutateAsync({
        id: String(selectedCode?.id) ?? '',
        data: formValues,
      });
    }
  };

  const handleDeleteButtonClick = () => {
    // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
    const message = new NoticeMessage(t('msg > delete confirm'), {
      mode: 'confirm',
    });
    message.confirmClicked().then(() => {
      deleteCodeMutation.mutateAsync(selectedCode?.id ?? '');
    });
  };

  const handleReset = () => {
    setSelectedIsCodeGroup('');
  };

  const logs = codeData
    ? [
        {
          label: t('cmn > registered by'),
          value: codeData.registered_by ?? '',
        },
        {
          label: t('cmn > registered time'),
          value: codeData.registered_time
            ? formatFullDateTimeDDMMYYYY(new Date(codeData.registered_time))
            : '',
        },
        { label: t('cmn > updated by'), value: codeData.updated_by ?? '' },
        {
          label: t('cmn > updated time'),
          value: codeData.updated_time
            ? formatFullDateTimeDDMMYYYY(new Date(codeData.updated_time))
            : '',
        },
      ]
    : [];

  return (
    <>
      <section className="wrap">
        <div className="header-title">
          <h3>{t('SYSTEM')}</h3>
          <h3>&gt;</h3>
          <h3>{t('SYSTEM-CODE')}</h3>
        </div>

        <ContainerCard>
          <Filtering
            searchRef={searchRef}
            placeholder={
              t('user > group code') +
              ' / ' +
              t('code > code') +
              ' / ' +
              t('code > code name') +
              ' / ' +
              t('code > description')
            }
            onSearch={handleSearch}
            onReset={handleReset}
            labelSelect={t('code > code group')}
            optionsRadioFilter={optionsRadioFilterDeleted}
            optionsRadioFilterUsage={optionsRadioFilterUsage}
            isUsage={true}
          >
            <Select
              value={selectedIsCodeGroup || ''}
              onChange={handleOnChangeInputSelect}
              className={'custom-select'}
              options={
                activeCodes
                  ? [
                      { value: '', label: t('cmn > all') },
                      ...activeCodes
                        .filter((item) => item.upper_code === item.id)
                        .map((item) => ({
                          value: item.upper_code,
                          label: item.eng,
                        })),
                    ]
                  : []
              }
            />
          </Filtering>
        </ContainerCard>
      </section>
      <ContainerCard>
        <ReactTabulator
          className="container-tabullator"
          id="code-management-table"
          data={codesData || []}
          columns={columnsHistory as unknown as ColumnDefinition[]}
          layout={'fitDataStretch'}
          options={optionsTabulator}
          onRef={(r) => {
            tbRef.current = r.current;
          }}
          events={{
            rowClick: (_e: UIEvent, row: Tabulator.RowComponent) => handleRowSelected(row),
            tableBuilt: () => {
              if (selectedCode?.id) {
                const row = tbRef.current?.getRow(selectedCode?.id) || null;
                row?.select();
                row?.getElement().classList.add('custom-hover');
              } else if (newId) {
                const row = tbRef.current?.getRow(newId) || null;
                if (row) {
                  tbRef.current?.setPageToRow(row);
                  tbRef.current?.scrollToRow(row, 'bottom', true);
                  row.select();
                  row.getElement().classList.add('custom-hover');
                  tbRef.current?.selectRow([newId]);
                  handleRowSelected(row);
                }
              }
            },
          }}
        />
      </ContainerCard>
      <ContainerCard>
        <div className="flex w-full flex-col gap-[10px]">
          <div className="grid grid-cols-3 gap-4">
            <div className="flex flex-col gap-4">
              <DetailForm
                label={t('code > code type')}
                value={formValues.codeType}
                inputType="select"
                onChange={handleInputChange}
                name="codeType"
                disabled={disabled}
                optionSelect={[
                  {
                    value: 'Code Group',
                    label: t('code > code group'),
                    disabled: disabledCodeGroup,
                  },
                  {
                    value: 'Code',
                    label: t('code > code'),
                    disabled: disabledCode,
                  },
                ]}
              />
              <DetailForm
                label={t('code > code group')}
                value={formValues.upper_code || ''}
                inputType="text"
                onChange={handleInputChange}
                name="upper_code"
                required={true}
                disabled={disabledCodeGroup}
                maxLength={50}
                formRef={codeGroupRef}
              />
              <DetailForm
                label={t('code > code')}
                value={formValues.lower_code || ''}
                inputType="text"
                onChange={handleInputChange}
                name="lower_code"
                required={true}
                disabled={disabledCode}
                maxLength={50}
                formRef={codeRef}
              />
            </div>

            <div className="flex flex-col gap-4">
              <DetailForm
                label={t('code > ENG name')}
                value={formValues.eng || ''}
                inputType="text"
                onChange={handleInputChange}
                name="eng"
                required={true}
                disabled={disabled}
              />
              <DetailForm
                label={t('code > IND name')}
                value={formValues.ind || ''}
                inputType="text"
                onChange={handleInputChange}
                name="ind"
                required={true}
                disabled={disabled}
              />
              <DetailForm
                label={t('code > usage')}
                value={formValues.usage || ''}
                inputType="select"
                onChange={handleInputChange}
                name="usage"
                required={true}
                disabled={disabled}
                optionSelect={
                  commonListData?.['002']
                    ? [
                        ...(commonListData['002']?.code?.map((code, index) => ({
                          value: code ?? '',
                          label: commonListData['002']?.name[index] ?? '',
                        })) || []),
                      ]
                    : []
                }
              />
            </div>

            <div className="flex flex-col gap-4">
              <DetailForm
                label={t('code > sort order')}
                value={formValues.sort_order || ''}
                inputType="number"
                onChange={handleInputChange}
                name="sort_order"
                disabled={disabled}
              />
              <DetailForm
                label={t('code > description')}
                value={formValues.description || ''}
                inputType="textarea"
                onChange={handleInputChange}
                name="description"
                disabled={disabled}
                className={'flex-grow'}
              />
            </div>
          </div>
          <hr className="border-t border-gray-300" />
          <div className="flex items-center justify-between gap-4 w-full">
            <div className="flex-1">{disabled ? null : <LogList logs={logs} />}</div>

            <div className="flex-none">
              <ButtonGroup
                onClickDelete={handleDeleteButtonClick}
                onClickNew={handleNewButtonClick}
                onClickCancel={handleCancelButtonClick}
                onClickRegist={handleRegistButtonClick}
                onClickConfirm={handleConfirmButtonClick}
                isNewClicked={isNewClicked}
                cancelButtonState={buttonState.cancel}
                confirmButtonState={hasChangesUpdate ? false : buttonState.confirm}
                deleteButtonState={buttonState.delete}
                newButtonState={hasChangesCreate ? false : buttonState.create}
              />
            </div>
          </div>
        </div>
      </ContainerCard>
    </>
  );
};

export default CodeManagement;
